import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '../useApiMutation';
import { contactKeys, creditMemoKeys, invoiceKeys } from '@/lib/queryKeys';
import { creditMemoEndpoints } from '@/lib/api/endpoints';
import { apiPost, operationsClient } from '@/lib/api/client';
import type { VoidCreditMemoInput } from '@/types/creditMemo';

interface VoidResult {
  id: string;
  status: string;
}

interface UseVoidArgs {
  id: string;
  input?: VoidCreditMemoInput;
}

export function useVoidCreditMemo() {
  const queryClient = useQueryClient();

  return useApiMutation<VoidResult, UseVoidArgs>({
    mutationFn: ({ id, input }) =>
      apiPost<VoidResult, VoidCreditMemoInput>(
        operationsClient,
        creditMemoEndpoints.void(id),
        input ?? {},
      ),
    onSuccess: () => {
      // A memo can reverse credit on several invoices. The response only
      // identifies the memo, so refresh invoice and customer financial views
      // even when the affected records aren't currently open or cached here.
      // Invalidation marks cached queries stale immediately. Do not keep a
      // completed void pending while unrelated active views finish fetching.
      // Include available balances and suggestions derived from credit memos.
      void queryClient.invalidateQueries({ queryKey: creditMemoKeys.all() });
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.all() });
      void queryClient.invalidateQueries({ queryKey: contactKeys.all() });
      toast.success('Credit memo voided');
    },
  });
}
